/* =============================================================================
 *  Kreux Matrix — Legal · /privacy  (Datenschutzerklärung)
 *  Standalone route · designed to open in a new tab from the Terminal footer.
 *  Bilingual EN/DE via global useLang() · zero hardcoded brand-palette hex.
 *  Brand vars only · pure Tailwind transitions · zero React state.
 * ===========================================================================*/

import { useLang } from "@/lib/LangContext";
import LegalLangToggle from "@/components/LegalLangToggle";

const dict = {
  en: {
    Title: "Privacy Policy",
    Subtitle: "Last Updated: June 2026",
    Eyebrow: "GDPR · Article 13/14 Disclosure",
    CloseTab: "Close Tab",
    BackToSite: "Back to Kreux Matrix",
    Sections: [
      {
        heading: "1. Data Protection at a Glance & Your Rights",
        body: "We take the protection of your personal data very seriously. This site uses SSL/TLS encryption for security. You have the right at any time to receive free information about the origin, recipient, and purpose of your stored data. You also have the right to request the correction, deletion, or restriction of this data. Furthermore, you have the right to revoke any given consent and the right to lodge a complaint with the competent supervisory authority."
      },
      {
        heading: "2. Responsible Party",
        body: "The responsible party for data processing on this website is:\n[Kreux Matrix Ai Studio]\n[Mannheim]\nGermany\n\nEmail: [Intelligence@kreuxmatrix.de]\n\nThe responsible party is the natural or legal person who alone or jointly with others decides on the purposes and means of processing personal data."
      },
      {
        heading: "3. Hosting",
        body: "Our website is securely hosted on servers located within the European Union. We prioritize your privacy and operate strictly under the regulations of the GDPR."
      },
      {
        heading: "4. Privacy-First Analytics",
        body: "We use privacy-friendly analytics. This tool operates entirely without cookies, does not collect personal data or IP addresses, and ensures no marketing data is transferred to the USA. Therefore, a cookie consent banner is not required."
      },
      {
        heading: "5. Lead Processing & Appointment Booking",
        body: "To manage inquiries and consultations, we utilize Google Workspace (Google Sheets) and Calendly (Calendly LLC). When you submit a form or book an appointment, your provided data is processed to fulfill your request (Art. 6(1)(b) GDPR). Data transfers to the US associated with these services are safeguarded by the EU-US Data Privacy Framework and Standard Contractual Clauses."
      }
    ]
  },
  de: {
    Title: "Datenschutzerklärung",
    Subtitle: "Zuletzt aktualisiert: Juni 2026",
    Eyebrow: "DSGVO · Artikel 13/14 Information",
    CloseTab: "Tab Schließen",
    BackToSite: "Zurück zu Kreux Matrix",
    Sections: [
      {
        heading: "1. Datenschutz auf einen Blick & Ihre Rechte",
        body: "Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Diese Seite nutzt aus Sicherheitsgründen eine SSL- bzw. TLS-Verschlüsselung. Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung, Sperrung oder Löschung dieser Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde sowie das Recht auf Widerruf erteilter Einwilligungen zu."
      },
      {
        heading: "2. Verantwortliche Stelle",
        body: "Verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:\n[Kreux Matrix Ai Studio]\n[Mannheim]\nDeutschland\n\nE-Mail: [Intelligence@kreuxmatrix.de]\n\nVerantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung personenbezogener Daten entscheidet."
      },
      {
        heading: "3. Hosting",
        body: "Unsere Website wird sicher auf Servern innerhalb der Europäischen Union gehostet. Wir legen höchsten Wert auf Ihre Privatsphäre und arbeiten streng nach den Vorgaben der DSGVO."
      },
      {
        heading: "4. Datenschutzfreundliche Analyse",
        body: "Wir nutzen eine datenschutzfreundliche Analysesoftware. Dieses Tool arbeitet vollständig ohne Cookies, erfasst keine personenbezogenen Daten und übermittelt keine Marketingdaten in die USA. Ein Cookie-Consent-Banner ist daher nicht erforderlich."
      },
      {
        heading: "5. Lead-Verarbeitung & Terminbuchung",
        body: "Zur Verwaltung von Anfragen und Beratungen nutzen wir Google Workspace (Google Sheets) sowie Calendly (Calendly LLC). Wenn Sie ein Formular absenden oder einen Termin buchen, werden Ihre Daten zur Bearbeitung Ihres Anliegens verarbeitet (Art. 6 Abs. 1 lit. b DSGVO). Damit verbundene Datenübermittlungen in die USA sind durch den EU-US Data Privacy Framework und Standardvertragsklauseln (SCCs) abgesichert."
      }
    ]
  }
};

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

const HexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-pp" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-pp)" />
  </svg>
);

const PrivacyPolicy = () => {
  const { lang } = useLang();
  const t = dict[lang] || dict.en;

  return (
    <main
      data-testid="privacy-policy-page"
      className="relative w-full min-h-screen font-sans overflow-x-hidden"
      style={{
        "--il-white": "#FFFFFF",
        "--il-cyan":  "#00E5FF",
        "--il-steel": "#1B3245",
        "--il-navy":  "#112332",
        "--il-amber": "#FFB300",
        background:
          "linear-gradient(180deg, #050B12 0%, #0A1520 40%, #03070C 100%)",
        color: "#FFFFFF",
      }}
    >
      {/* Hex matrix bg */}
      <div className="absolute inset-0 z-0 opacity-25 pointer-events-none animate-pulse" style={{ animationDuration: "8s" }}>
        <HexMatrix />
      </div>

      {/* In-page EN/DE toggle for direct-link visitors */}
      <LegalLangToggle />

      {/* Ambient cyan glow blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[var(--il-cyan)]/5 blur-[120px] z-0"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[10%] right-[-120px] w-[420px] h-[420px] rounded-full bg-[var(--il-cyan)]/5 blur-[120px] z-0"
      />

      {/* Top nav strip */}
      <nav
        data-testid="pp-nav"
        className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 py-8 flex items-center justify-between"
      >
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 no-underline group"
          data-testid="pp-brand"
        >
          <img
            src={LOGO_URL}
            alt="Kreux Matrix"
            className="w-8 h-8 object-contain select-none"
            draggable={false}
          />
          <span className="text-xl md:text-2xl font-light tracking-tighter text-white uppercase">
            Kreux Matrix
          </span>
        </a>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pp-back-link"
          className="text-xs font-mono text-white/50 hover:text-[var(--il-cyan)] tracking-[0.2em] uppercase transition-colors duration-300 no-underline"
        >
          ← {t.BackToSite}
        </a>
      </nav>

      {/* Content card */}
      <section className="relative z-10 w-full max-w-4xl mx-auto px-6 lg:px-12 pb-24 pt-8">
        <div
          data-testid="pp-card"
          className="relative rounded-3xl bg-white/5 backdrop-blur-3xl border border-[var(--il-cyan)]/20 shadow-[0_0_80px_-20px_var(--il-cyan)] hover:shadow-[0_0_120px_-20px_var(--il-cyan)] hover:border-[var(--il-cyan)]/40 transition-all duration-700 ease-out p-10 md:p-16 overflow-hidden"
        >
          {/* Inner cyan top-edge highlight */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(0,229,255,0.6), transparent)",
            }}
          />

          {/* Eyebrow */}
          <span
            className="block font-mono text-[10px] tracking-[0.32em] uppercase text-[var(--il-cyan)]/80 mb-4"
            data-testid="pp-eyebrow"
          >
            {t.Eyebrow}
          </span>

          {/* Title — metallic etched */}
          <h1
            data-testid="pp-title"
            className="text-5xl md:text-7xl font-light tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white/80 to-white/20 drop-shadow-[0_0_30px_rgba(255,255,255,0.1)] leading-tight pb-2 mb-4 m-0"
          >
            {t.Title}
          </h1>

          {/* Subtitle */}
          <p
            className="text-sm font-mono text-white/40 tracking-widest uppercase m-0 mb-10"
            data-testid="pp-subtitle"
          >
            {t.Subtitle}
          </p>

          {/* Divider hairline */}
          <span
            aria-hidden="true"
            className="block w-16 h-px mb-12"
            style={{
              background: "linear-gradient(90deg, var(--il-cyan), transparent)",
              boxShadow: "0 0 12px var(--il-cyan)",
            }}
          />

          {/* Sections */}
          <div className="flex flex-col gap-12" data-testid="pp-sections">
            {t.Sections.map((s, i) => (
              <article key={i} data-testid={`pp-section-${i}`}>
                <h2 className="text-sm md:text-base font-mono tracking-[0.22em] uppercase text-[var(--il-cyan)] mb-5 leading-snug">
                  {s.heading}
                </h2>
                <div className="flex flex-col gap-4">
                  {s.body.split("\n\n").map((para, j) => (
                    <p
                      key={j}
                      className="text-base md:text-lg text-white/70 font-light leading-relaxed m-0 whitespace-pre-line break-words"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          {/* Close-Tab CTA */}
          <div className="mt-16 pt-10 border-t border-white/10 flex items-center justify-center">
            <button
              type="button"
              data-testid="pp-close-tab"
              onClick={() => window.close()}
              className="relative group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md text-sm font-mono uppercase tracking-[0.22em] text-white/70 hover:text-[var(--il-cyan)] hover:border-[var(--il-cyan)]/60 hover:bg-[var(--il-cyan)]/[0.06] hover:shadow-[0_0_40px_-10px_var(--il-cyan)] transition-all duration-500 ease-out cursor-pointer"
            >
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rotate-45 bg-[var(--il-cyan)] shadow-[0_0_10px_var(--il-cyan)] group-hover:scale-125 transition-transform duration-500"
              />
              <span>{t.CloseTab}</span>
            </button>
          </div>
        </div>

        {/* Footer mini-bar */}
        <div className="mt-10 text-center">
          <span className="text-[10px] font-mono text-white/30 tracking-widest uppercase">
            © 2026 Kreux Matrix · All rights reserved
          </span>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
